import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Row,
  Col,
  Tooltip,
} from "antd";
import SchoolApi from "./api";

const STATUS_TAG = {
  1: <Tag color="green">Aktif</Tag>,
  2: <Tag color="orange">Pasif</Tag>,
  3: <Tag color="red">Silindi</Tag>,
};

const SchoolAdd = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [recordingId, setRecordingId] = useState(null);

  const load = (q) => {
    setLoading(true);
    SchoolApi.listSchools(q)
      .then((data) => setList(data || []))
      .catch((e) => message.error(e?.response?.data?.message || "Okullar getirilemedi"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = (values) => {
    setSaving(true);
    SchoolApi.createSchool({ name: values.name?.trim(), code: values.code?.trim() })
      .then(() => {
        message.success("Okul eklendi");
        form.resetFields();
        load(search);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Okul eklenemedi"))
      .finally(() => setSaving(false));
  };

  const startEdit = (row) => {
    setEditing(row);
    editForm.setFieldsValue({ name: row.name, code: row.code });
  };

  const onEditSave = (values) => {
    SchoolApi.updateSchool(editing.id, {
      name: values.name?.trim(),
      code: values.code?.trim() || null,
    })
      .then(() => {
        message.success("Okul güncellendi");
        setEditing(null);
        load(search);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Güncellenemedi"));
  };

  const toggleStatus = (row) => {
    SchoolApi.updateSchool(row.id, { status: row.status === 1 ? 2 : 1 })
      .then(() => load(search))
      .catch((e) => message.error(e?.response?.data?.message || "İşlem başarısız"));
  };

  // Okulun görüşme kaydı talebi; okula bağlı tüm öğrencilere uygulanır
  const toggleRecording = (row) => {
    const enabled = !row.recording_enabled;
    setRecordingId(row.id);
    SchoolApi.setSchoolRecording(row.id, enabled)
      .then(() => {
        message.success(`${row.name}: görüşme kaydı ${enabled ? "açıldı" : "kapatıldı"}`);
        load(search);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Görüşme kaydı ayarı kaydedilemedi"))
      .finally(() => setRecordingId(null));
  };

  const onDelete = (row, force = false) => {
    SchoolApi.deleteSchool(row.id, force)
      .then(() => {
        message.success("Okul silindi");
        load(search);
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || "Silinemedi";
        if (e?.response?.status === 409) {
          Modal.confirm({
            title: `"${row.name}" okulunu sil`,
            content: `${msg}\n\nYine de silmek istiyor musunuz? Bağlı sınıf, şube ve öğrenci atamaları da pasife alınır.`,
            okText: "Yine de sil",
            okButtonProps: { danger: true },
            cancelText: "Vazgeç",
            onOk: () => onDelete(row, true),
          });
        } else {
          message.error(msg);
        }
      });
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 70 },
    {
      title: "Okul Adı",
      dataIndex: "name",
      render: (v, row) =>
        editing && editing.id === row.id ? (
          <Form form={editForm} onFinish={onEditSave} layout="inline">
            <Form.Item name="name" rules={[{ required: true, message: "Zorunlu" }]} style={{ marginBottom: 0 }}>
              <Input placeholder="Okul adı" />
            </Form.Item>
            <Form.Item name="code" style={{ marginBottom: 0 }}>
              <Input placeholder="Kod" style={{ width: 100 }} />
            </Form.Item>
            <Space>
              <Button type="primary" size="small" htmlType="submit">
                Kaydet
              </Button>
              <Button size="small" onClick={() => setEditing(null)}>
                Vazgeç
              </Button>
            </Space>
          </Form>
        ) : (
          v
        ),
    },
    { title: "Kod", dataIndex: "code", width: 110, render: (v) => v || "-" },
    { title: "Sınıf", dataIndex: "grade_count", width: 90 },
    { title: "Şube", dataIndex: "branch_count", width: 90 },
    { title: "Öğrenci", dataIndex: "student_count", width: 90 },
    {
      title: "Görüşme Kaydı",
      dataIndex: "recording_enabled",
      width: 190,
      render: (v, row) => (
        <Space>
          <Tooltip
            title={
              row.recording_updated_at
                ? `Son değişiklik: ${new Date(row.recording_updated_at).toLocaleString("tr-TR")}${
                    row.recording_updated_by_name ? ` · ${row.recording_updated_by_name}` : ""
                  }`
                : "Henüz değiştirilmedi"
            }
          >
            {v ? <Tag color="green">Aktif</Tag> : <Tag>Pasif</Tag>}
          </Tooltip>
          <Popconfirm
            title={
              v
                ? `"${row.name}" öğrencileri için görüşme kaydı kapatılsın mı?`
                : `"${row.name}" okuluna bağlı tüm öğrenciler (${row.student_count}) için görüşme kaydı açılsın mı?`
            }
            okText={v ? "Kapat" : "Aç"}
            cancelText="Vazgeç"
            onConfirm={() => toggleRecording(row)}
          >
            <Button size="small" loading={recordingId === row.id}>
              {v ? "Kapat" : "Aç"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
    { title: "Durum", dataIndex: "status", width: 110, render: (v) => STATUS_TAG[v] || v },
    {
      title: "İşlem",
      width: 260,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => startEdit(row)}>
            Düzenle
          </Button>
          <Button size="small" onClick={() => toggleStatus(row)}>
            {row.status === 1 ? "Pasifleştir" : "Aktifleştir"}
          </Button>
          <Popconfirm title="Okul silinsin mi?" onConfirm={() => onDelete(row)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid p-3">
      <h1 className="h3 mb-3">
        <strong>Okul Ekle</strong>
      </h1>

      <Card title="Yeni Okul" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={onCreate}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Okul Adı" name="name" rules={[{ required: true, message: "Okul adı zorunludur" }]}>
                <Input placeholder="Örn. Özel Qarint Koleji" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Kısa Kod (opsiyonel)" name="code">
                <Input placeholder="Örn. IST01" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button type="primary" htmlType="submit" loading={saving} block>
                Kaydet
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title={`Okullar (${list.length})`}
        extra={
          <Input.Search
            placeholder="Okul ara"
            allowClear
            onSearch={(v) => {
              setSearch(v);
              load(v);
            }}
            style={{ width: 240 }}
          />
        }
      >
        <Table rowKey="id" size="small" loading={loading} dataSource={list} columns={columns} pagination={{ pageSize: 20 }} />
      </Card>
    </div>
  );
};

export default SchoolAdd;
